import { useEffect, useState } from 'react';
import type { TenantRole } from '@kioskforge/shared';
import { Badge, Button, Input, Sheet, StatusPip } from '../../components/ui';
import { ConfirmDialog, ErrorState } from '../../components/states';
import { useToast } from '../../app/ToastProvider';
import { pb } from '../../api/pocketbase';
import {
  renameDevice,
  regroupDevice,
  revokeDevice,
  sendCommand,
  type DeviceCommandType,
  type DeviceRecord
} from '../../api/devices';
import { lockdownLabel, relativeTime, statusPresentation } from './deviceView';

const COMMANDS: Array<{ type: DeviceCommandType; label: string; destructive?: boolean }> = [
  { type: 'reload', label: 'Reload' },
  { type: 'clear_cache', label: 'Clear cache' },
  { type: 'update_config', label: 'Update config' },
  { type: 'restart', label: 'Restart' },
  { type: 'rotate_token', label: 'Rotate token', destructive: true }
];

type AuditRow = { id: string; action?: string; created?: string; meta?: { type?: string } };

type Props = {
  device: DeviceRecord | null;
  role: TenantRole;
  onClose: () => void;
  onChanged: () => void;
};

export function DeviceDetail({ device, role, onClose, onChanged }: Props) {
  const { notify } = useToast();
  const [name, setName] = useState(device?.name ?? '');
  const [group, setGroup] = useState(device?.group ?? '');
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [error, setError] = useState('');
  const writable = role !== 'viewer';

  useEffect(() => {
    setName(device?.name ?? '');
    setGroup(device?.group ?? '');
    setError('');
  }, [device]);

  useEffect(() => {
    if (!device || role === 'viewer') return;
    let active = true;
    void pb
      .collection('audit_log')
      .getList(1, 20, { filter: `target_type = "device" && target_id = "${device.id}"`, sort: '-created' })
      .then((result) => {
        if (active) setAudit(result.items as AuditRow[]);
      })
      .catch(() => {
        if (active) setAudit([]);
      });
    return () => {
      active = false;
    };
  }, [device, role]);

  if (!device) return null;

  const status = statusPresentation(device.status);
  const capabilities = device.capabilities;

  async function runCommand(type: DeviceCommandType) {
    if (!device) return;
    try {
      await sendCommand(device.id, type);
      notify(`${type} queued for ${device.name}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Command failed.');
    }
  }

  async function saveName() {
    if (!device || !name.trim() || name === device.name) return;
    try {
      await renameDevice(device.id, name.trim());
      notify('Device renamed');
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Rename failed.');
    }
  }

  async function saveGroup() {
    if (!device || group === (device.group ?? '')) return;
    try {
      await regroupDevice(device.id, group.trim());
      notify('Group updated');
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Regroup failed.');
    }
  }

  return (
    <Sheet open title={`Device ${device.name ?? device.id}`} onClose={onClose}>
      {error && <ErrorState title="Action failed" detail={error} />}

      <div className="detail-status">
        <StatusPip status={status.pip} label={status.label} />
        <Badge>{capabilities?.device_class ?? 'unknown'}</Badge>
        <span className="mono">last seen {relativeTime(device.last_seen)}</span>
        {/* CC-6: the id lives under a Details disclosure, not in the header. */}
        <details className="row-details">
          <summary>Details</summary>
          <span className="mono">{device.id}</span>
        </details>
      </div>

      <section className="detail-section">
        <span className="index-label">CAPABILITIES</span>
        {capabilities ? (
          <dl className="kv">
            <dt>Class</dt>
            <dd>{capabilities.device_class}</dd>
            <dt>Mode</dt>
            <dd>{lockdownLabel(capabilities.lockdown)}</dd>
            <dt>Screen</dt>
            <dd>
              {capabilities.screen_w}×{capabilities.screen_h}
            </dd>
            <dt>Touch</dt>
            <dd>{capabilities.touch ? 'yes' : 'no'}</dd>
            <dt>Blocks</dt>
            <dd>{capabilities.blocks.join(', ') || 'none reported'}</dd>
          </dl>
        ) : (
          <p>No capabilities reported yet.</p>
        )}
      </section>

      <section className="detail-section">
        <span className="index-label">TELEMETRY</span>
        <dl className="kv">
          <dt>App version</dt>
          <dd>{device.app_version ?? 'unknown'}</dd>
          <dt>Config version</dt>
          <dd>{device.config_version ?? 0}</dd>
          <dt>IP</dt>
          <dd>{device.ip ?? 'unknown'}</dd>
          <dt>Battery</dt>
          <dd>{typeof device.battery === 'number' ? `${device.battery}%` : 'n/a'}</dd>
        </dl>
      </section>

      {writable && (
        <section className="detail-section">
          <span className="index-label">COMMANDS</span>
          <div className="command-row">
            {COMMANDS.map((command) => (
              <Button
                key={command.type}
                variant="quiet"
                type="button"
                disabled={device.status === 'revoked'}
                onClick={() => (command.destructive ? setConfirmRotate(true) : void runCommand(command.type))}
              >
                {command.label}
              </Button>
            ))}
          </div>
          <p className="detail-note">
            Commands are delivered on the device's next poll. Acknowledgement is recorded device-side.
          </p>
        </section>
      )}

      {role !== 'viewer' && audit.length > 0 && (
        <section className="detail-section">
          <span className="index-label">RECENT COMMANDS</span>
          <ul className="audit-list">
            {audit.map((row) => (
              <li key={row.id}>
                <span className="mono">{relativeTime(row.created)}</span> {row.action}
                {row.meta?.type ? ` (${row.meta.type})` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}

      {writable && (
        <section className="detail-section">
          <span className="index-label">IDENTITY</span>
          <label className="index-label" htmlFor="device-name">
            NAME
          </label>
          <Input id="device-name" value={name} onChange={(event) => setName(event.target.value)} onBlur={saveName} />
          <label className="index-label" htmlFor="device-group">
            GROUP
          </label>
          <Input id="device-group" value={group} onChange={(event) => setGroup(event.target.value)} onBlur={saveGroup} />
          <div className="detail-danger">
            <Button variant="danger" type="button" onClick={() => setConfirmRevoke(true)}>
              Revoke device
            </Button>
            <span>Revoking returns the device to pairing.</span>
          </div>
        </section>
      )}

      <ConfirmDialog
        open={confirmRevoke}
        title="Revoke device"
        confirmWord={device.name ?? 'revoke'}
        detail="The device loses its token and returns to pairing. Queued submissions are kept."
        confirmLabel="Revoke"
        onClose={() => setConfirmRevoke(false)}
        onConfirm={() => {
          setConfirmRevoke(false);
          void revokeDevice(device.id)
            .then(() => {
              notify('Device revoked');
              onChanged();
            })
            .catch((cause) => setError(cause instanceof Error ? cause.message : 'Revoke failed.'));
        }}
      />

      <ConfirmDialog
        open={confirmRotate}
        title="Rotate token"
        confirmWord="rotate"
        detail="The device is issued a new token on its next poll. The old token stops working immediately."
        confirmLabel="Rotate"
        onClose={() => setConfirmRotate(false)}
        onConfirm={() => {
          setConfirmRotate(false);
          void runCommand('rotate_token');
        }}
      />
    </Sheet>
  );
}
